/** An HTTP agent's package is what the person declares: the inputs each call takes, a task, an output schema. */

import type { AgentSpec, ExternalRef, InputField } from "@/features/agents/types";
import type { ResolvedTarget, TargetDraft } from "../types";
import { newInputRow, type InputRow } from "./input-fields-editor";

/** `null` for blank, the parsed object, or an error message. */
export function parseSchema(text: string | undefined): Record<string, unknown> | null | string {
  if (!text?.trim()) return null;
  try {
    const value: unknown = JSON.parse(text);
    if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
    return "The output schema must be a JSON object.";
  } catch {
    return "The output schema is not valid JSON.";
  }
}

/** The rows shown before the person edits any: one text input called "question". */
const DEFAULT_ROWS = [newInputRow("question")];
export const rowsOf = (t: TargetDraft) => t.inputs ?? DEFAULT_ROWS;

export function declaredInputs(t: TargetDraft): (InputField & { kind: "text" | "json" })[] {
  return rowsOf(t)
    .filter((r) => r.name.trim())
    .map(({ name, kind, required, description }) => ({
      name: name.trim(),
      kind,
      required,
      description: description.trim(),
      description_source: "",
      target: "",
      key: name.trim(),
      options: [],
    }));
}

export function declaredSpec(t: ResolvedTarget): AgentSpec {
  const schema = parseSchema(t.schemaText);
  return {
    name: t.agentName || t.path || "HTTP endpoint",
    instructions: t.task?.trim() || null,
    inputs: declaredInputs(t),
    model: null,
    params: {},
    tools: [],
    output_schema: typeof schema === "string" ? null : schema,
    run_settings: [],
    version: null,
    raw: {},
  };
}

/** A saved HTTP agent's ref back into the form's fields. */
export function fromRef(ref: ExternalRef): Partial<TargetDraft> {
  const inputs = Array.isArray(ref["inputs"]) ? (ref["inputs"] as InputRow[]) : [];
  const schema = ref["output_schema"];
  return {
    ...(typeof ref["path"] === "string" ? { path: ref["path"] } : {}),
    ...(inputs.length ? { inputs: inputs.map((i) => ({ ...newInputRow(i.name), ...i })) } : {}),
    ...(typeof ref["task"] === "string" ? { task: ref["task"] } : {}),
    ...(schema && typeof schema === "object" ? { schemaText: JSON.stringify(schema, null, 2) } : {}),
  };
}
